import React, { useState } from 'react';
import api from '../../services/api';

const TaskManagementModule = ({ employes, setEmployes, setTasks, onTaskCreated }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('all'); // 'today', 'week', 'month', 'all'

  // Fonction de rafraîchissement accessible
  const refreshTasks = React.useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.tasks.getAll();
      const apiTasks = res.tasks || [];
      setEmployes(prev => prev.map(emp => ({
        ...emp,
        assignedTasks: apiTasks
          .filter(t => t.employee_id === emp.user_id)
          .map(t => ({
            id: t.id,
            titre: t.titre || t.text || '—',
            priorite: t.priority || 'normale',
            categorie: t.category || 'Autre',
            dateEcheance: t.deadline || '',
            statut: t.done ? 'termine' : (t.statut || 'a_faire'),
            progression: t.done ? 100 : (t.progression || 0),
            done: !!t.done,
            document_preuve: t.document_preuve || null,
            completed_at: t.completed_at || null,
            employee: t.employee || null,
          }))
      })));
      console.log('✅ Tâches rafraîchies', { totalTasks: apiTasks.length });
    } catch (e) {
      console.error('❌ Erreur refresh tasks:', e);
    } finally {
      setRefreshing(false);
    }
  }, [setEmployes]);

  // Rafraîchir les tâches depuis l'API toutes les 30 secondes
  React.useEffect(() => {
    refreshTasks();
    const interval = setInterval(refreshTasks, 30000);
    return () => clearInterval(interval);
  }, [refreshTasks]);
  const [taskForm, setTaskForm] = useState({
    titre: '',
    description: '',
    priorite: 'normale',
    dateEcheance: '',
    categorie: 'Autre'
  });

  // Calculer les statistiques globales
  const totalTasks = employes.reduce((sum, emp) => sum + (emp.assignedTasks || []).length, 0);
  const completedTasks = employes.reduce((sum, emp) => 
    sum + (emp.assignedTasks || []).filter(t => t.statut === 'termine').length, 0
  );
  const inProgressTasks = employes.reduce((sum, emp) => 
    sum + (emp.assignedTasks || []).filter(t => t.statut === 'en_cours').length, 0
  );
  const overdueTasks = employes.reduce((sum, emp) => 
    sum + (emp.assignedTasks || []).filter(t => {
      if (!t.dateEcheance || t.statut === 'termine') return false;
      return new Date(t.dateEcheance) < new Date();
    }).length, 0
  );

  const getProgressionEmploye = (employe) => {
    const tasks = employe.assignedTasks || [];
    if (tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.statut === 'termine').length;
    return Math.round((completed / tasks.length) * 100);
  };

  const handleAssignTask = async () => {
    if (!taskForm.titre.trim() || !selectedEmployee) return;

    setLoading(true);

    try {
      // S'assurer qu'on envoie le bon user_id
      // Priorité: user_id > id (si id semble être un user_id) > recherche dans localStorage
      let employeeUserId = selectedEmployee.user_id;
      
      if (!employeeUserId) {
        // Fallback: Si pas de user_id, vérifier si id est le user_id
        // (dans certains cas, id peut déjà être le user_id)
        console.warn('⚠️ selectedEmployee.user_id manquant, utilisation de id:', selectedEmployee.id);
        employeeUserId = selectedEmployee.id;
      }
      
      const payload = {
        titre: taskForm.titre,
        description: taskForm.description || '',
        employee_id: employeeUserId,
        priority: taskForm.priorite || 'normale',
        category: taskForm.categorie || 'Autre',
        deadline: taskForm.dateEcheance || null,
      };

      console.log('📤 Création tâche - Payload:', payload);
      console.log('📤 selectedEmployee:', selectedEmployee);

      const response = await api.tasks.create(payload);
      const createdTask = response.task || response;

      console.log('✅ Tâche créée en BDD:', createdTask);

      const mappedTask = {
        id: createdTask.id,
        _id: createdTask.id,
        text: createdTask.titre || createdTask.text || taskForm.titre,
        done: !!createdTask.done,
        date: createdTask.deadline ? createdTask.deadline.slice(0, 10) : '',
        priorite: createdTask.priority || createdTask.priorite || 'normale',
        categorie: createdTask.category || createdTask.categorie || 'Autre',
        employee_id: createdTask.employee_id,
        employee: createdTask.employee,
        statut: createdTask.statut || 'a_faire',
        progression: 0,
        dateCreation: createdTask.created_at || new Date().toISOString(),
        titre: createdTask.titre || taskForm.titre,
        description: createdTask.description || taskForm.description,
        dateEcheance: createdTask.deadline || taskForm.dateEcheance,
      };

      // Mettre à jour le state local
      setEmployes(prev => prev.map(emp => {
        if (emp.id === selectedEmployee.id) {
          return {
            ...emp,
            assignedTasks: [...(emp.assignedTasks || []), mappedTask]
          };
        }
        return emp;
      }));

      if (typeof setTasks === 'function') {
        setTasks(prev => [...prev, mappedTask]);
      }

      // 🔄 RECHARGER LES TÂCHES DEPUIS L'API
      console.log("🔄 Rechargement des tâches depuis l'API...");
      try {
        const tasksResponse = await api.tasks.getAll();
        console.log('📥 Tâches reçues:', tasksResponse);

        if (tasksResponse.tasks && typeof setTasks === 'function') {
          // Mapper les tâches reçues
          const mappedTasks = tasksResponse.tasks.map(t => ({
            id: t.id,
            _id: t.id,
            text: t.titre || t.text || '—',
            done: !!t.done,
            date: t.deadline ? t.deadline.slice(0, 10) : '',
            priorite: t.priority || t.priorite || 'normale',
            categorie: t.category || t.categorie || 'Autre',
            employee_id: t.employee_id,
            employee: t.employee,
            statut: t.statut || 'a_faire',
            progression: 0,
            titre: t.titre || '',
            description: t.description || '',
            dateEcheance: t.deadline || '',
          }));

          setTasks(mappedTasks);
          console.log('✅ Tâches mises à jour:', mappedTasks.length);
        }
      } catch (refreshError) {
        console.error('⚠️ Erreur rechargement tâches:', refreshError);
      }

      // Appeler le callback si fourni
      if (typeof onTaskCreated === 'function') {
        onTaskCreated();
      }

      // Reset form
      setTaskForm({
        titre: '',
        description: '',
        priorite: 'normale',
        dateEcheance: '',
        categorie: 'Autre'
      });
      setShowModal(false);
      setSelectedEmployee(null);

    } catch (error) {
      console.error('❌ Erreur création tâche:', error);
      alert('Erreur lors de la création de la tâche: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = (employeId, taskId, newStatus) => {
    setEmployes(prev => prev.map(emp => {
      if (emp.id === employeId) {
        return {
          ...emp,
          assignedTasks: emp.assignedTasks.map(t => 
            t.id === taskId ? { ...t, statut: newStatus, progression: newStatus === 'termine' ? 100 : t.progression } : t
          )
        };
      }
      return emp;
    }));
  };

  const updateTaskProgression = (employeId, taskId, progression) => {
    setEmployes(prev => prev.map(emp => {
      if (emp.id === employeId) {
        return {
          ...emp,
          assignedTasks: emp.assignedTasks.map(t => 
            t.id === taskId ? { 
              ...t, 
              progression: Math.min(100, Math.max(0, progression)),
              statut: progression === 100 ? 'termine' : progression > 0 ? 'en_cours' : 'a_faire'
            } : t
          )
        };
      }
      return emp;
    }));
  };

  const deleteTask = async (employeId, taskId) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    
    try {
      // Appel API pour supprimer la tâche en base de données
      await api.tasks.delete(taskId);
      console.log('✅ Tâche supprimée de la base de données:', taskId);
      
      // Mettre à jour le state local après la suppression réussie
      setEmployes(prev => prev.map(emp => {
        if (emp.id === employeId) {
          return {
            ...emp,
            assignedTasks: emp.assignedTasks.filter(t => t.id !== taskId)
          };
        }
        return emp;
      }));

      // Mettre à jour aussi la liste globale des tâches si elle existe
      if (typeof setTasks === 'function') {
        setTasks(prev => prev.filter(t => t.id !== taskId));
      }

      // Recharger les tâches depuis l'API pour synchroniser
      try {
        const tasksResponse = await api.tasks.getAll();
        if (tasksResponse.tasks && typeof setTasks === 'function') {
          const mappedTasks = tasksResponse.tasks.map(t => ({
            id: t.id,
            _id: t.id,
            text: t.titre || t.text || '—',
            done: !!t.done,
            date: t.deadline ? t.deadline.slice(0, 10) : '',
            priorite: t.priority || t.priorite || 'normale',
            categorie: t.category || t.categorie || 'Autre',
            employee_id: t.employee_id,
            employee: t.employee,
            statut: t.statut || 'a_faire',
            progression: 0,
            titre: t.titre || '',
            description: t.description || '',
            dateEcheance: t.deadline || '',
          }));
          setTasks(mappedTasks);
        }
      } catch (refreshError) {
        console.warn('⚠️ Erreur lors du rechargement des tâches:', refreshError);
      }

    } catch (error) {
      console.error('❌ Erreur lors de la suppression de la tâche:', error);
      alert('Erreur lors de la suppression de la tâche: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const prioriteColor = {
    urgente: '#ef5350',
    haute: '#ff9800',
    normale: '#42a5f5',
    basse: '#78909c'
  };

  const statutLabels = {
    a_faire: { label: 'À faire', color: '#78909c', icon: '⏳' },
    en_cours: { label: 'En cours', color: '#ff9800', icon: '🔄' },
    termine: { label: 'Terminé', color: '#4caf50', icon: '✓' },
    bloque: { label: 'Bloqué', color: '#ef5350', icon: '🚫' }
  };

  // Fonction de filtrage des tâches par date
  const filterTasksByDate = (tasks) => {
    if (dateFilter === 'all') return tasks;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return tasks.filter(task => {
      if (!task.dateEcheance) return dateFilter === 'all';

      const taskDate = new Date(task.dateEcheance);
      const taskDay = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());

      switch (dateFilter) {
        case 'today':
          return taskDay.getTime() === today.getTime();
        
        case 'week':
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay() + 1); // Lundi
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6); // Dimanche
          return taskDay >= weekStart && taskDay <= weekEnd;
        
        case 'month':
          return taskDay.getMonth() === today.getMonth() && 
                 taskDay.getFullYear() === today.getFullYear();
        
        default:
          return true;
      }
    });
  };

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Header avec bouton refresh */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 16 
      }}>
        <h3 style={{ 
          fontSize: 18, 
          fontWeight: 600, 
          color: '#fff',
          margin: 0 
        }}>
          📋 Gestion des Tâches
        </h3>
        <button
          onClick={refreshTasks}
          disabled={refreshing}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            background: refreshing ? 'rgba(100,181,246,0.1)' : 'rgba(100,181,246,0.15)',
            border: '1px solid rgba(100,181,246,0.3)',
            borderRadius: 8,
            color: '#64b5f6',
            fontSize: 12,
            fontWeight: 500,
            cursor: refreshing ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => !refreshing && (e.target.style.background = 'rgba(100,181,246,0.25)')}
          onMouseOut={(e) => !refreshing && (e.target.style.background = 'rgba(100,181,246,0.15)')}
        >
          <span style={{ 
            display: 'inline-block',
            animation: refreshing ? 'spin 1s linear infinite' : 'none'
          }}>🔄</span>
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Filtre par date */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        padding: 4,
        background: '#0a1520',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 10,
        width: 'fit-content'
      }}>
        {[
          { value: 'all', label: '📅 Toutes', icon: '📅' },
          { value: 'today', label: '📍 Aujourd\'hui', icon: '📍' },
          { value: 'week', label: '📆 Cette semaine', icon: '📆' },
          { value: 'month', label: '🗓️ Ce mois', icon: '🗓️' }
        ].map(filter => (
          <button
            key={filter.value}
            onClick={() => setDateFilter(filter.value)}
            style={{
              padding: '8px 14px',
              background: dateFilter === filter.value ? 'linear-gradient(135deg, #C9A84C, #8a6028)' : 'transparent',
              border: dateFilter === filter.value ? 'none' : '1px solid rgba(255,255,255,0.08)',
              borderRadius: 7,
              color: dateFilter === filter.value ? '#fff' : '#667',
              fontSize: 11,
              fontWeight: dateFilter === filter.value ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              if (dateFilter !== filter.value) {
                e.target.style.background = 'rgba(255,255,255,0.03)';
                e.target.style.color = '#fff';
              }
            }}
            onMouseOut={(e) => {
              if (dateFilter !== filter.value) {
                e.target.style.background = 'transparent';
                e.target.style.color = '#667';
              }
            }}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* KPIs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 14,
        marginBottom: 24
      }}>
        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12,
          padding: 18
        }}>
          <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>📋 Total Tâches</div>
          <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", color: '#fff' }}>
            {totalTasks}
          </div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12,
          padding: 18
        }}>
          <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>✓ Terminées</div>
          <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", color: '#4caf50' }}>
            {completedTasks}
          </div>
          <div style={{ fontSize: 10, color: '#667', marginTop: 4 }}>
            {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}% du total
          </div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12,
          padding: 18
        }}>
          <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>🔄 En cours</div>
          <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", color: '#ff9800' }}>
            {inProgressTasks}
          </div>
        </div>

        <div style={{
          background: '#111f30',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 12,
          padding: 18
        }}>
          <div style={{ fontSize: 11, color: '#667', marginBottom: 8 }}>⚠️ En retard</div>
          <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "'Cormorant Garamond', serif", color: '#ef5350' }}>
            {overdueTasks}
          </div>
        </div>
      </div>

      {/* Liste des employés avec leurs tâches */}
      <div style={{
        background: '#111f30',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 14,
        padding: 22,
        marginBottom: 20
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', marginBottom: 4 }}>
              👥 Tâches par Employé
            </div>
            <div style={{ fontSize: 11, color: '#667' }}>
              Affectation et suivi de progression
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {employes.map(emp => {
            const allTasks = emp.assignedTasks || [];
            const tasks = filterTasksByDate(allTasks); // Appliquer le filtre
            const progression = getProgressionEmploye(emp);
            const progressColor = progression >= 70 ? '#4caf50' : progression >= 40 ? '#ff9800' : '#ef5350';

            return (
              <div key={emp.id} style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: 10,
                padding: 16
              }}>
                {/* Header employé */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: emp.color || '#42a5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 700,
                      color: '#fff'
                    }}>
                      {emp.av}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>
                        {emp.nom && emp.prenom !== emp.nom && !emp.prenom.includes(emp.nom) ? `${emp.prenom} ${emp.nom}` : emp.prenom}
                      </div>
                      <div style={{ fontSize: 10, color: '#667' }}>
                        {emp.poste} · {tasks.length} tâche{tasks.length !== 1 ? 's' : ''} {dateFilter !== 'all' && allTasks.length !== tasks.length && `(${allTasks.length} total)`}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {/* Progression */}
                    <div style={{ width: 120 }}>
                      <div style={{ fontSize: 9, color: '#667', marginBottom: 4, textAlign: 'right' }}>
                        Progression: {progression}%
                      </div>
                      <div style={{
                        height: 6,
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: 99,
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${progression}%`,
                          height: '100%',
                          background: progressColor,
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>

                    {/* Bouton assigner */}
                    <button
                      onClick={() => {
                        setSelectedEmployee(emp);
                        setShowModal(true);
                      }}
                      disabled={loading}
                      style={{
                        background: loading ? '#555' : 'linear-gradient(135deg, #C9A84C, #8a6028)',
                        border: 'none',
                        borderRadius: 7,
                        padding: '8px 16px',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 600,
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.6 : 1
                      }}
                    >
                      {loading ? '⏳...' : '+ Assigner tâche'}
                    </button>
                  </div>
                </div>

                {/* Liste des tâches */}
                {tasks.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tasks.map(task => {
                      const isOverdue = task.dateEcheance && new Date(task.dateEcheance) < new Date() && task.statut !== 'termine';
                      const statutInfo = statutLabels[task.statut];

                      return (
                        <div key={task.id} style={{
                          background: '#0a1520',
                          border: `1px solid ${isOverdue ? 'rgba(239,83,80,0.3)' : 'rgba(255,255,255,0.04)'}`,
                          borderRadius: 8,
                          padding: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12
                        }}>
                          {/* Priorité */}
                          <div style={{
                            width: 4,
                            height: 40,
                            borderRadius: 2,
                            background: prioriteColor[task.priorite],
                            flexShrink: 0
                          }} />

                          {/* Contenu */}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4
                            }}>
                              <div style={{ fontSize: 12, fontWeight: 500, color: '#fff' }}>
                                {task.titre}
                              </div>
                              <div style={{
                                fontSize: 9,
                                padding: '2px 6px',
                                borderRadius: 4,
                                background: prioriteColor[task.priorite] + '22',
                                color: prioriteColor[task.priorite],
                                textTransform: 'uppercase',
                                fontWeight: 600
                              }}>
                                {task.priorite}
                              </div>
                              {isOverdue && (
                                <div style={{
                                  fontSize: 9,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  background: 'rgba(239,83,80,0.15)',
                                  color: '#ef5350',
                                  fontWeight: 600
                                }}>
                                  ⚠️ En retard
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: '#667' }}>
                              {task.categorie} · Échéance: {task.dateEcheance ? new Date(task.dateEcheance).toLocaleDateString('fr-FR') : 'Non définie'}
                            </div>
                          </div>

                          {/* Statut — lecture seule, mis à jour par l'employé */}
                          <div style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            background: statutInfo.color + '22',
                            border: `1px solid ${statutInfo.color}44`,
                            color: statutInfo.color,
                            fontSize: 11,
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            {statutInfo.icon} {statutInfo.label}
                          </div>

                          {/* Voir la preuve */}
                          {task.document_preuve && (
                            <button
                              onClick={() => {
                                const directUrl = `https://fhcgroupesarl.com/plateforme/${task.document_preuve}`;
                                console.log('🔍 Ouverture document depuis TaskManagement:', directUrl);
                                window.open(directUrl, '_blank');
                              }}
                              style={{
                                background: 'rgba(66,165,245,0.1)',
                                border: '1px solid rgba(66,165,245,0.3)',
                                borderRadius: 6,
                                padding: '6px 10px',
                                color: '#42a5f5',
                                fontSize: 11,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              📄 Voir preuve
                            </button>
                          )}

                          {/* Supprimer */}
                          <button
                            onClick={() => deleteTask(emp.id, task.id)}
                            style={{
                              background: 'rgba(239,83,80,0.1)',
                              border: '1px solid rgba(239,83,80,0.3)',
                              borderRadius: 6,
                              padding: '6px 10px',
                              color: '#ef5350',
                              fontSize: 11,
                              cursor: 'pointer'
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px 0',
                    color: '#667',
                    fontSize: 11
                  }}>
                    Aucune tâche assignée
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal d'assignation de tâche */}
      {showModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }} onClick={() => setShowModal(false)}>
          <div style={{
            background: '#162535',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 14,
            padding: 28,
            width: 500,
            maxWidth: '90%'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              fontSize: 16,
              fontWeight: 600,
              color: '#fff',
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>✅ Assigner une tâche</span>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#889',
                  fontSize: 24,
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            {selectedEmployee && (
              <div style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 8,
                padding: 12,
                marginBottom: 20,
                display: 'flex',
                alignItems: 'center',
                gap: 10
              }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: selectedEmployee.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff'
                }}>
                  {selectedEmployee.av}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                    {selectedEmployee.nom && selectedEmployee.prenom !== selectedEmployee.nom && !selectedEmployee.prenom.includes(selectedEmployee.nom) ? `${selectedEmployee.prenom} ${selectedEmployee.nom}` : selectedEmployee.prenom}
                  </div>
                  <div style={{ fontSize: 10, color: '#667' }}>
                    ID: {selectedEmployee.id} | user_id: {selectedEmployee.user_id || 'N/A'} | {selectedEmployee.poste}
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{
                  fontSize: 10,
                  color: '#889',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  display: 'block',
                  marginBottom: 6
                }}>
                  Titre de la tâche *
                </label>
                <input
                  type="text"
                  value={taskForm.titre}
                  onChange={(e) => setTaskForm({...taskForm, titre: e.target.value})}
                  placeholder="Ex: Préparer rapport mensuel"
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    color: '#fff',
                    fontSize: 13,
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontSize: 10,
                  color: '#889',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  display: 'block',
                  marginBottom: 6
                }}>
                  Description
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                  placeholder="Détails de la tâche..."
                  rows={3}
                  style={{
                    width: '100%',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    color: '#fff',
                    fontSize: 13,
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{
                    fontSize: 10,
                    color: '#889',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    display: 'block',
                    marginBottom: 6
                  }}>
                    Priorité
                  </label>
                  <select
                    value={taskForm.priorite}
                    onChange={(e) => setTaskForm({...taskForm, priorite: e.target.value})}
                    style={{
                      width: '100%',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="basse">Basse</option>
                    <option value="normale">Normale</option>
                    <option value="haute">Haute</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>

                <div>
                  <label style={{
                    fontSize: 10,
                    color: '#889',
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    display: 'block',
                    marginBottom: 6
                  }}>
                    Date échéance
                  </label>
                  <input
                    type="date"
                    value={taskForm.dateEcheance}
                    onChange={(e) => setTaskForm({...taskForm, dateEcheance: e.target.value})}
                    style={{
                      width: '100%',
                      background: '#0d1b2a',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  fontSize: 10,
                  color: '#889',
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  display: 'block',
                  marginBottom: 6
                }}>
                  Catégorie
                </label>
                <select
                  value={taskForm.categorie}
                  onChange={(e) => setTaskForm({...taskForm, categorie: e.target.value})}
                  style={{
                    width: '100%',
                    background: '#0d1b2a',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    padding: '10px 12px',
                    color: '#fff',
                    fontSize: 13,
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <option value="Pilotage">Pilotage</option>
                  <option value="Projet">Projet</option>
                  <option value="Admin">Admin</option>
                  <option value="Finance">Finance</option>
                  <option value="RH">RH</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: 10,
              marginTop: 24,
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowModal(false)}
                disabled={loading}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 8,
                  padding: '10px 20px',
                  color: '#889',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleAssignTask}
                disabled={loading}
                style={{
                  background: loading ? '#555' : 'linear-gradient(135deg, #C9A84C, #8a6028)',
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 20px',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? '⏳ Création...' : '✓ Assigner la tâche'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TaskManagementModule;
